<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div>
    <v-container class="trWorkFileExportCronList">
      <v-row></v-row>
      <v-row>
        <v-data-table
          style="width: 100%;"
          :headers="headers"
          :items="itemList"
          :footer-props="{
            'items-per-page-options': [5, 10, 15, 25, -1],
          }"
          :items-per-page="25"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          dense
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>TR Work File Export Jobs</v-toolbar-title>
              <v-spacer></v-spacer>
              <TrWorkFileExportDialog
                :closeDialogBox.sync="closeDialogBox"
                @settingDialogVal="settingDialogVal"
                :baseCrudKey="baseCrudKey"
                :rowItem.sync="clickedItem"
              />
              <v-btn
                class="mx-2" color="primary"
                fab icon small
                @click="newItem"
              >
                <v-icon>{{ icons.mdiPlus }}</v-icon>
              </v-btn>
            </v-toolbar>
          </template>
          <template v-slot:item.isActiveInactive="{ item }">
            <v-checkbox v-model="item.isActiveInactive" readonly></v-checkbox>
          </template>
          <template v-slot:item.params.profileName="{ item }">
            <span class="caption">{{ item.params && item.params.profileName }}</span>
          </template>
          <template v-slot:item.params.exportProfile="{ item }">
            <span class="caption">{{ item.params && item.params.exportProfile }}</span>
          </template>
          <template v-slot:item.lastRunStatus="{ item }">
            <v-tooltip bottom v-if="item.lastRunStatus === 'failed'">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-icon small color="error">{{ icons.mdiAlertCircle }}</v-icon>
                  <span class="caption error--text ml-1">
                    Failed{{ item.lastErrorStage ? (' (' + item.lastErrorStage + ')') : '' }}
                  </span>
                </span>
              </template>
              <span class="caption">
                {{ item.lastError || 'No error detail recorded' }}
                <br />
                Attempted: {{ item.lastAttemptTime }}
              </span>
            </v-tooltip>
            <span v-else-if="item.lastRunStatus === 'success'">
              <v-icon small color="success">{{ icons.mdiCheckCircle }}</v-icon>
              <span class="caption ml-1">OK</span>
            </span>
            <span v-else class="caption grey--text">Never run</span>
          </template>
          <template v-slot:item.lastRunTime="{ item }">
            {{ formatLastRun(item.lastRunTime) }}
          </template>
          <template v-slot:item.trigger="{ item }">
            <v-btn small @click="triggerNow(item)" :disabled="!item.isActiveInactive">
              Trigger Now
            </v-btn>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" @click="openDialog(item)">{{ icons.mdiPencil }}</v-icon>
            <v-icon small @click="deleteItem(item)">{{ icons.mdiDelete }}</v-icon>
          </template>
        </v-data-table>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import {
  mdiPencil, mdiDelete, mdiPlus, mdiCheckCircle, mdiAlertCircle,
} from '@mdi/js';
import { describeCronDays } from '../../util/shared/cronDays';
import TrWorkFileExportDialog from './TrWorkFileExportDialog.vue';
import { clients } from '../../util/clients';
import { toCentralWallClockDateTime } from '../../util/shared/vue-global';

const { backendRest } = clients.direct;

export default {
  name: 'TrWorkFileExportCron',
  components: {
    TrWorkFileExportDialog,
  },
  data() {
    return {
      search: '',
      sortBy: [''],
      sortDesc: [false],
      closeDialogBox: false,
      clickedItem: {
        description: '',
        destination: '/tax/trWorkFile/profile-export',
        jobKind: 'trWorkFileExport',
        params: {
          profileName: '',
          exportProfile: 'DELQ TAX CSV',
          listName: '',
          wkstID: 0,
          targetPath: '/data/exports/f/ascii/over-night-reports/$TODAY',
        },
        outputFilename: 'UnpaidTaxRecords_$TODAY_MMDDYYYY.csv',
        expectedRunTime: {
          days: '*', hrs: 2, mins: 0, secs: 0,
        },
        isActiveInactive: false,
      },
      baseCrudKey: 'trWorkFileExportCronList',
      headers: [
        { text: 'Active/Inactive', value: 'isActiveInactive' },
        { text: 'Job Name', value: 'description' },
        { text: 'Profile', value: 'params.profileName' },
        { text: 'Export Profile', value: 'params.exportProfile' },
        { text: 'Expected Time', value: 'nextExpectedRunTime' },
        { text: 'Last Run Time', value: 'lastRunTime' },
        { text: 'Status', value: 'lastRunStatus', sortable: false },
        { text: 'Trigger', value: 'trigger' },
        { text: 'Actions', value: 'actions' },
      ],
      icons: {
        mdiPencil, mdiDelete, mdiPlus, mdiCheckCircle, mdiAlertCircle,
      },
    };
  },
  created() {
    this.loadList();
  },
  methods: {
    // The scheduler's Central wall-clock time, stored as if it were UTC - see
    // toCentralWallClockDateTime.
    formatLastRun(value) {
      return toCentralWallClockDateTime(value);
    },
    ...mapActions(['reAuth', 'flashWarn', 'flashSuccess']),
    ...mapActions('cron/cronJobUtil', ['getCronJobs', 'deleteCronJob']),
    ...mapMutations('base/crud', ['setCriteria', 'setItems', 'setItem']),
    openDialog(item) {
      this.closeDialogBox = true;
      this.clickedItem = { ...item };
      this.setItem([this.baseCrudKey, this.clickedItem]);
    },
    settingDialogVal(value) {
      this.closeDialogBox = value;
      this.loadList();
    },
    async loadList() {
      const list = await this.getCronJobs({
        criteria: { jobKind: 'trWorkFileExport' },
        baseCrudKey: this.baseCrudKey,
      });
      this.convertExpectedRunTime(list);
      return list;
    },
    convertExpectedRunTime(list) {
      const itemList = [...(list || [])];
      for (let i = 0; i < itemList.length; i += 1) {
        const { expectedRunTime } = itemList[i];
        if (!expectedRunTime) {
          // eslint-disable-next-line no-continue
          continue;
        }
        const { days } = expectedRunTime;
        const hrs = (expectedRunTime.hrs || 0).toString().padStart(2, '0');
        const mins = (expectedRunTime.mins || 0).toString().padStart(2, '0');
        const secs = (expectedRunTime.secs || 0).toString().padStart(2, '0');
        const dayString = describeCronDays(days);
        itemList[i].nextExpectedRunTime = `${dayString} ${hrs}:${mins}:${secs}`;
      }
      this.setItems([this.baseCrudKey, itemList]);
      return itemList;
    },
    newItem() {
      this.clickedItem = {
        description: '',
        destination: '/tax/trWorkFile/profile-export',
        jobKind: 'trWorkFileExport',
        params: {
          profileName: '',
          exportProfile: 'DELQ TAX CSV',
          listName: '',
          wkstID: 0,
          targetPath: '/data/exports/f/ascii/over-night-reports/$TODAY',
        },
        outputFilename: 'UnpaidTaxRecords_$TODAY_MMDDYYYY.csv',
        expectedRunTime: {
          days: '*', hrs: 2, mins: 0, secs: 0,
        },
        isActiveInactive: false,
      };
      this.closeDialogBox = true;
      this.openDialog(this.clickedItem);
    },
    async triggerNow(item) {
      const response = await backendRest.post('/executeCronJob', { ...item });
      if (response.status === 200) {
        this.flashSuccess('TR Work File export job triggered successfully');
        this.loadList();
      } else {
        this.flashWarn('Failed to trigger TR Work File export job');
      }
      return response;
    },
    deleteItem(item) {
      this.deleteCronJob({ itemId: item._id, baseCrudKey: this.baseCrudKey })
        .then(() => {
          this.flashSuccess('Job deleted successfully');
          this.loadList();
        })
        .catch((error) => {
          this.flashWarn(`Failed to delete job: ${error.message}`);
        });
    },
  },
  computed: {
    ...mapGetters('base/crud', ['criteria', 'items']),
    ...mapState({}),
    itemList() {
      return this.items(this.baseCrudKey) || [];
    },
  },
};
</script>
